import React, { useEffect, useRef, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { Button, Alert } from 'react-bootstrap';
import './contactusform.scss';
import './ContactUsZenwallet.scss';
import { t, translate } from 'react-switch-lang';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line import/no-unresolved
import { faMobileAlt, faWindow } from '@awesome.me/kit-299dd46f46/icons/classic/regular';
import { validateBody, validateName } from './Validation';
import { invalidFormAlert } from '../../../helperFunctions/Accessibility';
import RelevantFAQ from './RelevantFAQ';
import ContactFormHeader from './ContactFormHeader';
import useRelevantFAQSearch from '../../../helperFunctions/useRelevantFAQSearch';
import useContactFormSubmit from '../../../helperFunctions/useContactFormSubmit';
import {
  MailcheckEmailField,
  ContactRadioField,
  TextAreaField,
  TextField,
} from '../../../components/FormFields/FormFields';
import ContactReCaptcha from '../../../components/ContactReCaptcha';
import { getContactFormStyle } from '../../../configurations/Brands';
import {
  events,
  trackGoogleAnalyticsEvent,
} from '../../../helperFunctions/GoogleAnalytics';

const WEB_APPLICATION = 'Web';
const MOBILE_APPLICATION = 'Mobile';
const CARD_USAGE_CATEGORY = 'Card Usage';
const LOADING_ISSUES_CATEGORY = 'Loading Issues';
const ACCOUNT_DELETION_CATEGORY = 'Data or account deletion request';
const OTHER_CATEGORY = 'Other';
const CARD_TYPE = 'Canada Post Cash Passport™ Prepaid Mastercard';

function ContactUsZenwallet({ widgetMode, removeAccount }) {
  const { lang } = useSelector((state) => state);
  const cardUsageMessageRef = useRef();
  const [cardUsageSubmitted, setCardUsageSubmitted] = useState(false);
  const { handleTextareaChange, searchResults } = useRelevantFAQSearch({
    brand: 'zenwallet',
    lang,
  });
  const {
    dismissError,
    error,
    errorMessageRef,
    reCaptchaRef,
    showReCaptchaError,
    showError,
    submitted,
    submitContactRequest,
    successMessageRef,
  } = useContactFormSubmit();

  const isMobile = (window.location.href.includes('mobileapp') ? '?mobileapp' : '');
  const initialValues = removeAccount ? {
    application: MOBILE_APPLICATION,
    category: ACCOUNT_DELETION_CATEGORY,
  } : {};

  useEffect(() => {
    trackGoogleAnalyticsEvent(events.CONTACT_FORM_VIEW, {
      brand: 'zenwallet',
      language: lang,
    });
  }, [lang]);

  function validateContactForm(values) {
    const errors = {};
    const nameError = validateName(values.name || '');
    const descriptionError = validateBody(values.description || '');

    if (!values.application) errors.application = t('Validation_Required');
    if (!values.category) errors.category = t('Validation_Required');
    if (nameError) errors.name = nameError;
    if (descriptionError) errors.description = descriptionError;

    return errors;
  }

  async function sendAPI(values) {
    const data = {
      Brand: 'Zenwallet',
      Application: values.application,
      BrandID: values.application === WEB_APPLICATION ?
        'Zenwallet.ca website' :
        'Zenwallet mobile app',
      CustomerEmail: values.customerEmail,
      Language: lang,
      Category: values.category,
      CardType: CARD_TYPE,
      Body: values.description,
      Name: values.name,
    };

    return submitContactRequest(data);
  }

  useEffect(() => {
    if (widgetMode) {
      document.getElementById('desktop-contact-us').style.padding = '0px';
    }
  }, []);

  useEffect(() => {
    if (!cardUsageSubmitted || !cardUsageMessageRef.current) return;
    cardUsageMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [cardUsageSubmitted]);

  return (
    <>
      {showError && (
        <Alert
          ref={errorMessageRef}
          variant="danger"
          id="error_message"
          className="mt-3"
          onClose={dismissError}
          dismissible
        >
          <p style={{ marginBottom: 0 }}>{error}</p>
        </Alert>
      )}
      {submitted && (
        <div
          id="successMessage"
          ref={successMessageRef}
          style={{ display: 'block', visibility: 'visible' }}
        >
          <h1>{t('ContactForm_Submitted')}</h1>
          <Link to={(widgetMode) ? `/widget/zenwallet/${lang}${isMobile}` : `/zenwallet/${lang}/categories`}>
            <Button className="error-go-home-btn zenwallet-button">{t('ReturnHome_Btn')}</Button>
          </Link>
        </div>
      )}
      {cardUsageSubmitted && (
        <div
          id="card-usage-message"
          ref={cardUsageMessageRef}
          style={{ display: 'block', visibility: 'visible' }}
        >
          <h1 dangerouslySetInnerHTML={{ __html: t('Zenwallet_Card_Usage_Message') }} />
          <Link to={(widgetMode) ? `/widget/zenwallet/${lang}${isMobile}` : `/zenwallet/${lang}/categories`}>
            <Button className="error-go-home-btn zenwallet-button">{t('ReturnHome_Btn')}</Button>
          </Link>
        </div>
      )}
      {!submitted && !cardUsageSubmitted && (
        <div
          id="desktop-contact-us"
          className={widgetMode && 'widget-mode-form'}
          style={getContactFormStyle('zenwallet')}
        >
          <div id="skip-link-content">
            <ContactFormHeader brand="zenwallet" lang={lang} widgetMode={widgetMode} />
            <p className="contact-label-suggestion">{t('ContactForm_Fields_Required')}</p>
            <FinalForm
              initialValues={initialValues}
              onSubmit={sendAPI}
              validate={validateContactForm}
              render={({
                handleSubmit,
                invalid,
                errors,
                submitting,
                values,
                submitFailed,
                touched,
              }) => {
                const applicationError = errors.application;
                const showApplicationError = (
                  touched?.application || submitFailed
                ) && applicationError;
                const categoryError = errors.category;
                const showCategoryError = (
                  touched?.category || submitFailed
                ) && categoryError;

                return (
                  <form
                    onSubmit={(event) => {
                      if (values.category === CARD_USAGE_CATEGORY) {
                        event.preventDefault();
                        dismissError();
                        setCardUsageSubmitted(true);
                        return;
                      }
                      invalidFormAlert(invalid, errors);
                      handleSubmit(event);
                    }}
                    noValidate
                  >
                    <div className="row">
                      <MailcheckEmailField
                        className="col-12 col-md-6"
                        id="ContactForm_General_Email_"
                        name="customerEmail"
                        label={t('ContactForm_General_Email_Label')}
                        formatText={t('ContactForm_General_Email_Help')}
                        buttonContainerClassName="mailcheck-email-actions zenwallet-web-mobile"
                        buttonClassName="clickable-btn"
                      />
                    </div>

                    <fieldset className="zenwallet-radio form-field">
                      <legend
                        id="applicationLabel"
                        className={`field-label ${showApplicationError ? 'is-invalid' : ''}`}
                      >
                        {t('ContactForm_General_AppID_Label')}
                      </legend>
                      <div className="radio-container">
                        <ContactRadioField
                          name="application"
                          value={MOBILE_APPLICATION}
                          content={(
                            <>
                              <FontAwesomeIcon icon={faMobileAlt} className="btn-icon" />
                              {t('ContactForm_AppID_Mobile')}
                            </>
                        )}
                        />
                        <ContactRadioField
                          name="application"
                          value={WEB_APPLICATION}
                          content={(
                            <>
                              <FontAwesomeIcon icon={faWindow} className="btn-icon" />
                              {t('ContactForm_AppID_Website')}
                            </>
                        )}
                        />
                      </div>
                      {showApplicationError && (
                      <span className="errorMessage errorMessageBlock" id="app_error_message">{applicationError}</span>
                      )}
                    </fieldset>

                    <fieldset className="zenwallet-radio form-field">
                      <legend
                        id="categoryLabel"
                        className={`field-label ${showCategoryError ? 'is-invalid' : ''}`}
                      >
                        {t('ContactForm_General_Category_Label')}
                      </legend>
                      <div className="row no-gutters w-100">
                        <div className="col-12 col-md-6 mb-2 pr-md-1">
                          <ContactRadioField
                            name="category"
                            value={CARD_USAGE_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={(
                              <>
                                {' '}
                                {t('ContactForm_Category_Zenwallet_CardServices')}
                                <div style={{ fontSize: 'smaller' }}>
                                  {t('ContactForm_Category_Zenwallet_CardServices_Deets')}
                                </div>
                              </>
                          )}
                          />
                        </div>
                        <div className="col-12 col-md-6 mb-2 pl-md-1">
                          <ContactRadioField
                            name="category"
                            value={LOADING_ISSUES_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_Category_Zenwallet_LoadIssue')}
                            fixAlignment
                          />
                        </div>
                        <div className="col-12 col-md-6 mb-2 pr-md-1">
                          <ContactRadioField
                            name="category"
                            value={ACCOUNT_DELETION_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_Category_All_AccountDeletion')}
                            fixAlignment
                          />
                        </div>
                        <div className="col-12 col-md-6 mb-2 pl-md-1">
                          <ContactRadioField
                            name="category"
                            value={OTHER_CATEGORY}
                            className="w-100 h-100"
                            optionClassName="d-block w-100 h-100"
                            content={t('ContactForm_Category_General_Other')}
                            fixAlignment
                          />
                        </div>
                      </div>
                      {showCategoryError && (
                      <span className="errorMessage" id="category_error_message">{categoryError}</span>
                      )}
                    </fieldset>

                    <div className="row">
                      <TextField
                        className="col-12 col-md-6"
                        id="ContactForm_General_Name_"
                        name="name"
                        label={t('ContactForm_Name')}
                      />
                    </div>

                    <div className="row">
                      <TextAreaField
                        className="col-12"
                        id="ContactForm_General_Body_"
                        name="description"
                        label={t('ContactForm_General_Body_Label')}
                        rows={8}
                        onChange={handleTextareaChange}
                      >
                        <div className="sensitive-information-disclaimer">
                          {t('Sensitive_Information_Disclaimer')}
                        </div>
                      </TextAreaField>
                    </div>

                    <RelevantFAQ searchResults={searchResults} brand="zenwallet" lang={lang} />

                    <Button
                      id="submit-button"
                      className="zenwallet-button"
                      type="submit"
                      disabled={submitting}
                      size="lg"
                      style={{ backgroundColor: '#2B4E7B' }}
                    >
                      {submitting ? t('ContactForm_Submitting') : t('ContactForm_Submit_button')}
                    </Button>
                    <ContactReCaptcha ref={reCaptchaRef} onErrored={showReCaptchaError} />
                  </form>
                );
              }}
            />
            <Alert className="mt-3 email-banner" show={process.env.REACT_APP_ENV === 'dev'}>
              On the test system, contact form emails will be sent to
              <strong> psidevstar@gmail.com</strong>
            </Alert>
          </div>
        </div>
      )}
    </>
  );
}

export default translate(ContactUsZenwallet);
